import { createContext, useContext, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "../i18n/translations";

const LanguageContext = createContext(null);

export const LOCALES = ["en", "fr", "tr"];

// Picks a starting locale: stored preference → browser language → English.
export const detectLocale = () => {
  if (typeof window === "undefined") return "en";
  const stored = localStorage.getItem("vl_lang");
  if (LOCALES.includes(stored)) return stored;
  const nav = (navigator.language || navigator.userLanguage || "").slice(0, 2).toLowerCase();
  return LOCALES.includes(nav) ? nav : "en";
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // The active language is derived from the first URL segment (source of truth).
  const seg = location.pathname.split("/")[1];
  const lang = LOCALES.includes(seg) ? seg : "en";

  useEffect(() => {
    if (LOCALES.includes(seg)) localStorage.setItem("vl_lang", lang);
    document.documentElement.lang = lang;
  }, [lang, seg]);

  // Switches language by swapping the locale segment in the current URL.
  const setLang = useCallback(
    (next) => {
      if (!LOCALES.includes(next)) return;
      const parts = location.pathname.split("/");
      if (LOCALES.includes(parts[1])) parts[1] = next;
      else parts.splice(1, 0, next);
      localStorage.setItem("vl_lang", next);
      navigate((parts.join("/") || "/") + location.search + location.hash);
    },
    [location, navigate]
  );

  const t = useCallback(
    (path) => {
      const keys = path.split(".");
      let node = translations[lang];
      for (const k of keys) {
        node = node?.[k];
        if (node === undefined) return path;
      }
      return node;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
